// UI Utility Functions for consistent formatting across the app

/**
 * Format snake_case or SCREAMING_SNAKE_CASE to Title Case
 * e.g., "EQUIPMENT_MALFUNCTION" → "Equipment Malfunction"
 */
export const formatLabel = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/[_-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format camelCase to Title Case
 * e.g., "equipmentMalfunction" → "Equipment Malfunction"
 */
export const formatCamelCase = (str) => {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

/**
 * Clean up test/demo IDs for display
 * e.g., "TEST_Hospital_db61aa70" → "Hospital"
 * e.g., "DEMO-HOSP-ABC123" → "Demo Hospital"
 */
export const cleanDisplayName = (name) => {
  if (!name) return 'Unknown';
  
  // Remove common test prefixes
  let cleaned = name
    .replace(/^TEST_/i, '')
    .replace(/^DEMO[-_]/i, 'Demo ')
    .replace(/_[a-f0-9]{6,}$/i, '') // Remove trailing hex IDs
    .replace(/[-_][a-f0-9]{8,}$/i, ''); // Remove UUID-like suffixes
  
  // Format remaining underscores
  cleaned = cleaned.replace(/_/g, ' ');
  
  return cleaned.trim() || name;
};

/**
 * Format priority for display
 */
export const formatPriority = (priority) => {
  const priorities = {
    low: { label: 'Low', color: 'text-slate-600 bg-slate-100' },
    normal: { label: 'Normal', color: 'text-blue-600 bg-blue-100' },
    medium: { label: 'Medium', color: 'text-amber-600 bg-amber-100' },
    high: { label: 'High', color: 'text-orange-600 bg-orange-100' },
    urgent: { label: 'Urgent', color: 'text-red-600 bg-red-100' },
    critical: { label: 'Critical', color: 'text-red-700 bg-red-200' }
  };
  return priorities[priority?.toLowerCase()] || priorities.normal;
};

/**
 * Format status for display with consistent styling
 */
export const formatStatus = (status) => {
  const statuses = {
    pending: { label: 'Pending', color: 'text-amber-700 bg-amber-50 border-amber-200' },
    pending_approval: { label: 'Pending Approval', color: 'text-amber-700 bg-amber-50 border-amber-200' },
    in_progress: { label: 'In Progress', color: 'text-blue-700 bg-blue-50 border-blue-200' },
    assigned: { label: 'Assigned', color: 'text-purple-700 bg-purple-50 border-purple-200' },
    dispatched: { label: 'Dispatched', color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
    completed: { label: 'Completed', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    complete: { label: 'Complete', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    closed: { label: 'Closed', color: 'text-slate-700 bg-slate-100 border-slate-200' },
    cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 border-red-200' },
    on_hold: { label: 'On Hold', color: 'text-slate-600 bg-slate-100 border-slate-200' },
    needs_parts: { label: 'Needs Parts', color: 'text-orange-700 bg-orange-50 border-orange-200' },
    work_order_created: { label: 'Work Order Created', color: 'text-blue-700 bg-blue-50 border-blue-200' },
    hospital_approved: { label: 'Hospital Approved', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    hospital_rejected: { label: 'Hospital Rejected', color: 'text-red-700 bg-red-50 border-red-200' }
  };
  
  const key = status?.toLowerCase().replace(/ /g, '_');
  return statuses[key] || { label: formatLabel(status), color: 'text-slate-700 bg-slate-100 border-slate-200' };
};

/**
 * Format request type for display
 */
export const formatRequestType = (type) => {
  const types = {
    plumbing: { label: 'Plumbing', icon: '🔧', color: 'text-blue-700' },
    electrical: { label: 'Electrical', icon: '⚡', color: 'text-amber-700' },
    hvac: { label: 'HVAC', icon: '❄️', color: 'text-cyan-700' },
    equipment_malfunction: { label: 'Equipment Malfunction', icon: '⚙️', color: 'text-red-700' },
    general_maintenance: { label: 'General Maintenance', icon: '🔨', color: 'text-slate-700' },
    sterilization: { label: 'Sterilization', icon: '🧼', color: 'text-emerald-700' },
    water_system: { label: 'Water System', icon: '💧', color: 'text-blue-700' },
    other: { label: 'Other', icon: '📝', color: 'text-slate-600' }
  };
  
  const key = type?.toLowerCase().replace(/ /g, '_');
  return types[key] || { label: formatLabel(type), icon: '📋', color: 'text-slate-700' };
};

/**
 * Format date for display
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  // Relative time for recent dates
  if (!options.absolute) {
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  }
  
  // Format as date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    ...options
  });
};

/**
 * Format currency
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

export default {
  formatLabel,
  formatCamelCase,
  cleanDisplayName,
  formatPriority,
  formatStatus,
  formatRequestType,
  formatDate,
  formatCurrency,
  truncateText
};
